import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

// Componente de barra de progresso com cores
const ProgressBar = ({ value, maxValue = 100, colorClass = 'bg-blue-500' }) => {
  const percentage = Math.min((value / maxValue) * 100, 100);
  return (
    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
      <div 
        className={`h-full transition-all duration-500 rounded-full ${colorClass}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

// Função para determinar cor baseada no nível de risco
const getRiskColor = (nivel) => {
  switch(nivel) {
    case 'baixo': return { bg: 'bg-green-500', text: 'text-green-600', bgLight: 'bg-green-100' };
    case 'atencao': return { bg: 'bg-yellow-500', text: 'text-yellow-600', bgLight: 'bg-yellow-100' };
    case 'risco': return { bg: 'bg-red-500', text: 'text-red-600', bgLight: 'bg-red-100' };
    default: return { bg: 'bg-gray-500', text: 'text-gray-600', bgLight: 'bg-gray-100' };
  }
};

// Ícones para insights
const InsightIcon = ({ tipo }) => {
  const iconClass = "w-5 h-5";
  
  switch(tipo) {
    case 'critico':
      return (
        <svg className={`${iconClass} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    case 'atencao':
      return (
        <svg className={`${iconClass} text-yellow-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    default:
      return (
        <svg className={`${iconClass} text-blue-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

function DashboardControle({ getAuthHeaders }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard-controle/estatisticas`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados de controle');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="dashboard-controle-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const { status_financeiro, projecao, gastos_por_categoria, margem_investimento, insights, mes_referencia } = stats;
  const riskColors = getRiskColor(status_financeiro.nivel_risco);

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="dashboard-controle">
      <h1 className="text-3xl font-bold text-gray-900">Dashboard – Controle</h1>
      <p className="text-gray-600 text-sm">Painel decisório preventivo • {mes_referencia.nome_mes} {mes_referencia.ano}</p>

      {/* 1️⃣ Status Financeiro Mensal - Largura Total */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-testid="status-financeiro">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Status Financeiro Mensal
        </h2>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Renda comprometida com gastos</span>
            <span className={`text-sm font-bold ${riskColors.text}`}>
              {status_financeiro.percentual_comprometido}%
            </span>
          </div>
          <ProgressBar 
            value={status_financeiro.percentual_comprometido} 
            colorClass={riskColors.bg}
          />
        </div>

        <div className="flex items-center gap-2 mt-3">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${riskColors.bgLight} ${riskColors.text}`}>
            {status_financeiro.nivel_risco === 'baixo' && 'Baixo Risco'}
            {status_financeiro.nivel_risco === 'atencao' && 'Atenção'}
            {status_financeiro.nivel_risco === 'risco' && 'Risco Alto'}
          </span>
          <span className="text-sm text-gray-600">
            Você já comprometeu <strong>{status_financeiro.percentual_comprometido}%</strong> da sua renda líquida neste mês.
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
          <div>
            <p className="text-xs text-gray-500">Renda Líquida</p>
            <p className="text-lg font-semibold text-gray-900">{formatCurrency(status_financeiro.renda_liquida)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Gastos do Mês</p>
            <p className="text-lg font-semibold text-red-600">{formatCurrency(status_financeiro.gastos_mes)}</p>
          </div>
          <div className="col-span-2 md:col-span-1">
            <p className="text-xs text-gray-500">Disponível</p>
            <p className="text-lg font-semibold text-green-600">{formatCurrency(status_financeiro.renda_liquida - status_financeiro.gastos_mes)}</p>
          </div>
        </div>
      </div>

      {/* Layout 2 colunas para Projeção e Margem */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* 2️⃣ Projeção de Fim de Mês */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-testid="projecao-fim-mes">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Projeção de Fim de Mês
          </h2>

          {/* Gráfico simplificado de linha */}
          <div className="relative h-40 bg-gray-50 rounded-lg p-4 mb-4">
            <div className="absolute inset-4 flex flex-col justify-between">
              {/* Linha de referência da receita */}
              <div className="relative">
                <div className="absolute w-full border-t-2 border-dashed border-green-400" style={{ top: '0' }}></div>
                <span className="absolute -top-5 right-0 text-xs text-green-600 font-medium">Receita Total</span>
              </div>
              
              {/* Área do gráfico */}
              <div className="flex-1 flex items-end">
                <div className="flex items-end w-full h-full gap-1">
                  {/* Barra de gastos atuais */}
                  <div 
                    className="flex-1 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t transition-all duration-500"
                    style={{ 
                      height: `${Math.min((projecao.gastos_acumulados / projecao.receita_total) * 100, 100)}%`,
                      minHeight: projecao.gastos_acumulados > 0 ? '10px' : '0'
                    }}
                    title={`Gastos Atuais: ${formatCurrency(projecao.gastos_acumulados)}`}
                  />
                  {/* Barra de projeção */}
                  <div 
                    className="flex-1 bg-gradient-to-t from-orange-400 to-orange-300 rounded-t transition-all duration-500 opacity-70"
                    style={{ 
                      height: `${Math.min((projecao.projecao_fim_mes / projecao.receita_total) * 100, 100)}%`,
                      minHeight: projecao.projecao_fim_mes > 0 ? '10px' : '0'
                    }}
                    title={`Projeção: ${formatCurrency(projecao.projecao_fim_mes)}`}
                  />
                </div>
              </div>
            </div>
            
            {/* Legenda */}
            <div className="absolute bottom-2 left-4 right-4 flex justify-center gap-4 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-blue-500 rounded"></span>
                Atual (dia {mes_referencia.dia_atual})
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-orange-400 rounded"></span>
                Projeção
              </span>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            Mantendo o ritmo atual, seus gastos podem atingir <strong className={stats.percentual_projecao > 100 ? 'text-red-600' : 'text-orange-600'}>{stats.percentual_projecao}%</strong> da sua renda até o fim do mês.
          </p>
          
          <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-500">Gastos Atuais</p>
              <p className="font-semibold text-blue-600">{formatCurrency(projecao.gastos_acumulados)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Projeção Fim do Mês</p>
              <p className="font-semibold text-orange-600">{formatCurrency(projecao.projecao_fim_mes)}</p>
            </div>
          </div>
        </div>

        {/* 4️⃣ Compromisso com Investimento (Margem) */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-testid="margem-investimento">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Compromisso com Investimento
          </h2>

          {/* Gauge horizontal */}
          <div className="mb-4">
            <div className="relative h-8 bg-gray-100 rounded-full overflow-hidden">
              {/* Faixas de cor */}
              <div className="absolute inset-0 flex">
                <div className="w-1/4 bg-red-200 border-r border-white"></div>
                <div className="w-1/4 bg-yellow-200 border-r border-white"></div>
                <div className="w-1/4 bg-blue-200 border-r border-white"></div>
                <div className="w-1/4 bg-green-200"></div>
              </div>
              
              {/* Indicador */}
              <div 
                className="absolute top-0 bottom-0 w-1 bg-gray-800 transition-all duration-500"
                style={{ 
                  left: `${Math.min(Math.max(margem_investimento.valor, 0), 40) / 40 * 100}%`,
                  transform: 'translateX(-50%)'
                }}
              >
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                  {margem_investimento.valor}%
                </div>
              </div>
            </div>
            
            {/* Labels das faixas */}
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>&lt;10%</span>
              <span>10-20%</span>
              <span>20-30%</span>
              <span>&gt;30%</span>
            </div>
          </div>

          <div className={`p-3 rounded-lg ${
            margem_investimento.faixa === 'critico' ? 'bg-red-50 border border-red-200' :
            margem_investimento.faixa === 'melhorando' ? 'bg-yellow-50 border border-yellow-200' :
            margem_investimento.faixa === 'proximo' ? 'bg-blue-50 border border-blue-200' :
            'bg-green-50 border border-green-200'
          }`}>
            <p className={`text-sm ${
              margem_investimento.faixa === 'critico' ? 'text-red-700' :
              margem_investimento.faixa === 'melhorando' ? 'text-yellow-700' :
              margem_investimento.faixa === 'proximo' ? 'text-blue-700' :
              'text-green-700'
            }`}>
              {margem_investimento.texto}
            </p>
          </div>
        </div>
      </div>

      {/* 3️⃣ Controle de Gastos por Categoria */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-testid="gastos-por-categoria">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          Controle de Gastos por Categoria
        </h2>

        {gastos_por_categoria.length === 0 ? (
          <p className="text-gray-500 text-sm py-4 text-center">Nenhum gasto registrado neste mês.</p>
        ) : (
          <div className="space-y-4">
            {gastos_por_categoria.map((cat, index) => {
              const catColors = getRiskColor(cat.nivel_risco);
              return (
                <div key={index} className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">{cat.categoria}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{formatCurrency(cat.valor)}</span>
                      <span className={`text-xs font-semibold ${catColors.text}`}>
                        {cat.percentual}%
                      </span>
                    </div>
                  </div>
                  <ProgressBar 
                    value={cat.percentual} 
                    maxValue={30} 
                    colorClass={catColors.bg}
                  />
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-gray-500 mt-4 pt-4 border-t border-gray-100">
          Percentual representa a proporção do gasto em relação à renda líquida. Verde (&lt;10%), Amarelo (10-20%), Vermelho (&gt;20%).
        </p>
      </div>

      {/* 5️⃣ Ações Recomendadas */}
      {insights.length > 0 && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-testid="acoes-recomendadas">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            Ações Recomendadas
          </h2>

          <div className="space-y-3">
            {insights.map((insight, index) => (
              <div 
                key={index} 
                className={`flex items-start gap-3 p-3 rounded-lg ${
                  insight.tipo === 'critico' ? 'bg-red-50 border border-red-200' :
                  insight.tipo === 'atencao' ? 'bg-yellow-50 border border-yellow-200' :
                  'bg-blue-50 border border-blue-200'
                }`}
              >
                <InsightIcon tipo={insight.tipo} />
                <p className={`text-sm ${
                  insight.tipo === 'critico' ? 'text-red-700' :
                  insight.tipo === 'atencao' ? 'text-yellow-700' :
                  'text-blue-700'
                }`}>
                  {insight.texto}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <p className="text-sm text-gray-600 flex items-center">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Este dashboard exibe apenas visualização e análise dos dados já cadastrados. Nenhum dado é alterado.
        </p>
      </div>
    </div>
  );
}

export default DashboardControle;
